"use client";

const PENDING_VERIFICATION_EMAIL_KEY = "pendingVerificationEmail";
const PENDING_VERIFICATION_AUTH_KEY = "pendingVerificationAuth";
const PENDING_VERIFICATION_TTL_MS = 60 * 60 * 1000;

function canUseStorage() {
  return (
    typeof window !== "undefined" && typeof window.localStorage !== "undefined"
  );
}

function canUseSessionStorage() {
  return (
    typeof window !== "undefined" &&
    typeof window.sessionStorage !== "undefined"
  );
}

export function getPendingVerificationEmail(): string | null {
  if (!canUseStorage()) {
    return null;
  }

  return window.localStorage.getItem(PENDING_VERIFICATION_EMAIL_KEY);
}

export function setPendingVerificationEmail(email: string): void {
  if (!canUseStorage()) {
    return;
  }

  window.localStorage.setItem(
    PENDING_VERIFICATION_EMAIL_KEY,
    email.trim().toLowerCase(),
  );
}

export function clearPendingVerificationEmail(): void {
  if (!canUseStorage()) {
    return;
  }

  window.localStorage.removeItem(PENDING_VERIFICATION_EMAIL_KEY);
}

type PendingVerificationAuth = {
  email: string;
  password: string;
  createdAt: number;
};

export function setPendingVerificationAuth(input: {
  email: string;
  password: string;
}): void {
  if (!canUseSessionStorage()) {
    return;
  }

  window.sessionStorage.setItem(
    PENDING_VERIFICATION_AUTH_KEY,
    JSON.stringify({
      email: input.email.trim().toLowerCase(),
      password: input.password,
      createdAt: Date.now(),
    } satisfies PendingVerificationAuth),
  );
}

export function getPendingVerificationAuth(
  email?: string | null,
): PendingVerificationAuth | null {
  if (!canUseSessionStorage()) {
    return null;
  }

  const raw = window.sessionStorage.getItem(PENDING_VERIFICATION_AUTH_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as PendingVerificationAuth;
    const normalizedEmail = email?.trim().toLowerCase();

    if (
      !parsed?.email ||
      !parsed?.password ||
      !parsed?.createdAt ||
      Date.now() - parsed.createdAt > PENDING_VERIFICATION_TTL_MS ||
      (normalizedEmail && parsed.email !== normalizedEmail)
    ) {
      window.sessionStorage.removeItem(PENDING_VERIFICATION_AUTH_KEY);
      return null;
    }

    return parsed;
  } catch {
    window.sessionStorage.removeItem(PENDING_VERIFICATION_AUTH_KEY);
    return null;
  }
}

export function clearPendingVerificationAuth(): void {
  if (!canUseSessionStorage()) {
    return;
  }

  window.sessionStorage.removeItem(PENDING_VERIFICATION_AUTH_KEY);
}
